import React, { useState, useEffect } from 'react';

import { Tooltip, Fab } from "@mui/material";
import Grid from '@mui/material/Grid2';
import {
  DeleteForever,
} from "@mui/icons-material";

import { 
  Potence,
  UpgradableNumber
} from "@dndModels";

import SelectResourceBox from "@dndComponents/select/SelectResourceBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import StringBox from "@components/input/strings/StringBox";
import UpgradableNumberBox from "@dndComponents/UpgradableNumberBox";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import { CHARACTER_LEVELS } from "@dndModels/Constants";

interface Props {
  obj: Potence;
  slot_level: number;
  onChange: (changed: Potence) => void;
  onDelete: () => void;
  disabled?: boolean;
}

export default function AbilityPotenceCreateResourceInput({ 
  obj,
  slot_level,
  onChange,
  onDelete,
  disabled = false
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [basic, setBasic] = useState(false);

  // eslint-disable-next-line
  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setBasic(obj.create_resource.amount.flat.isBasic() && obj.create_resource.amount.count.isBasic() && obj.create_resource.amount.size.isBasic());
    }
  });

  return (
    <Grid container spacing={0} direction="row">
      <Grid
        size={{
          xs: 4
        }}>
        { !disabled &&
          <Tooltip title={`Delete Potence`}>
            <Fab size="small" color="primary" style={{marginLeft: "8px"}}
              onClick={ () => {
                onDelete();
              }}>
              <DeleteForever/>
            </Fab>
          </Tooltip>
        }
      </Grid>
      <Grid
        size={{
          xs: 4
        }}>
        <SelectStringBox 
          name="Potence Level"
          options={CHARACTER_LEVELS}
          value={`${obj.level}`} 
          disabled={disabled}
          onChange={(changed: string) => {
            const new_obj = obj;
            new_obj.level = +changed;
            onChange(new_obj);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 4
        }}>
        <SelectResourceBox 
          name="Resource Created"
          extra_options={["Slot","Charge","Heroic Inspiration"]}
          value={ obj.create_resource.resource_type }
          disabled={disabled}
          onChange={(changed: string) => {
            const new_obj = obj;
            new_obj.create_resource.resource_type = changed;
            onChange(new_obj);
          }}
        /> 
      </Grid>
      { obj.create_resource.resource_type === "Slot" &&
        <Grid
          size={{
            xs: 2
          }}>
          <SelectStringBox 
            name="Slot Type"
            value={ obj.create_resource.slot_type }
            options={["Any","Slots","Pact"]}
            disabled={disabled}
            onChange={(value: string) => {
              const new_obj = obj;
              new_obj.create_resource.slot_type = value;
              onChange(new_obj);
            }}
          /> 
        </Grid>
      }
      { obj.create_resource.resource_type === "Slot" &&
        <Grid
          size={{
            xs: 2
          }}>
          <SelectStringBox 
            name="Slot Level"
            value={ obj.create_resource.level }
            options={["Any","1","2","3","4","5","6","7","8","9"]}
            disabled={disabled}
            onChange={(value: string) => {
              const new_obj = obj;
              new_obj.create_resource.level = value;
              onChange(new_obj);
            }}
          /> 
        </Grid>
      }
      <Grid
        container
        spacing={0}
        direction="row"
        size={{
          xs: obj.create_resource.resource_type === "Slot" ? 8 : 12
        }}>
        {/* <UpgradableNumberBox 
          name="Amount"
          value={ obj.create_resource.amount }
          disabled={disabled}
          onChange={(value: UpgradableNumber) => {
            const new_obj = obj;
            new_obj.create_resource.amount = value.clone();
            onChange(new_obj);
          }}
        /> */}
        
        <Grid
          size={{
            xs: 2
          }}>
          <ToggleButtonBox 
            name="Basic"
            value={basic} 
            disabled={disabled}
            onToggle={() => {
              if (!basic) {
                const new_obj = obj;
                new_obj.create_resource.amount.flat.makeBasic();
                new_obj.create_resource.amount.count.makeBasic();
                new_obj.create_resource.amount.size.makeBasic();
                onChange(new_obj);
              }
              setBasic(!basic);
            }}
          />
        </Grid>
        { basic ? 
          <Grid
            size={{
              xs: 2
            }}>
            <StringBox 
              name="Flat"
              value={`${obj.create_resource.amount.flat.base}`} 
              disabled={disabled}
              type="number"
              onChange={(value: string) => {
                const new_obj = obj;
                new_obj.create_resource.amount.flat.base = +value;
                onChange(new_obj);
              }}
            />
          </Grid>
        :
          <Grid
            size={{
              xs: 2
            }}>
            <UpgradableNumberBox 
              name="Flat"
              value={obj.create_resource.amount.flat} 
              disabled={disabled}
              onChange={(value: UpgradableNumber) => {
                const new_obj = obj;
                new_obj.create_resource.amount.flat = value;
                onChange(new_obj);
              }}
            />
          </Grid>
        }
        { basic ? 
          <Grid
            size={{
              xs: 3
            }}>
            <StringBox 
              name="Dice Count"
              value={`${obj.create_resource.amount.count.base}`} 
              disabled={disabled}
              type="number"
              onChange={(value: string) => {
                const new_obj = obj;
                new_obj.create_resource.amount.flat.base = +value;
                onChange(new_obj);
              }}
            />
          </Grid>
        :
          <Grid
            size={{
              xs: 3
            }}>
            <UpgradableNumberBox 
              name="Dice Count"
              value={obj.create_resource.amount.count} 
              disabled={disabled}
              onChange={(value: UpgradableNumber) => {
                const new_obj = obj;
                new_obj.create_resource.amount.count = value;
                onChange(new_obj);
              }}
            />
          </Grid>
        }
        { basic ? 
          <Grid
            size={{
              xs: 3
            }}>
            <StringBox 
              name="Dice Size"
              value={`${obj.create_resource.amount.size.base}`} 
              disabled={disabled}
              type="number"
              onChange={(value: string) => {
                const new_obj = obj;
                new_obj.create_resource.amount.size.base = +value;
                onChange(new_obj);
              }}
            />
          </Grid>
        :
          <Grid
            size={{
              xs: 3
            }}>
            <UpgradableNumberBox 
              name="Dice Size"
              value={obj.create_resource.amount.size} 
              disabled={disabled}
              onChange={(value: UpgradableNumber) => {
                const new_obj = obj;
                new_obj.create_resource.amount.size = value;
                onChange(new_obj);
              }}
            />
          </Grid>
        }
        { obj.create_resource.resource_type !== "Slot" &&
          <>
            { basic ? 
              <Grid
                size={{
                  xs: 2
                }}>
                <StringBox 
                  name="Up to Max"
                  value={`${obj.create_resource.up_to_max.base}`} 
                  disabled={disabled}
                  type="number"
                  onBlur={(changed: string) => {
                    const new_obj = obj;
                    new_obj.create_resource.up_to_max.base = +changed;
                    onChange(new_obj);
                  }}
                />
              </Grid>
            :
              <Grid
                size={{
                  xs: 2
                }}>
                <UpgradableNumberBox 
                  name="Up to Max"
                  value={obj.create_resource.up_to_max} 
                  disabled={disabled}
                  onChange={(value: UpgradableNumber) => {
                    const new_obj = obj;
                    new_obj.create_resource.up_to_max = value;
                    onChange(new_obj);
                  }}
                />
              </Grid>
            }
          </>
        }
      </Grid>
    </Grid>
  );
}
